import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditAccountSelectField } from '../../../EditAccountSelectField';
import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import { EditFormField } from '../../../EditFormField';
import { EditTextareaField } from '../../../EditTextareaField';
import {
  type AccountOthersData,
  GET_ACCOUNT_OTHERS
} from '../queries/accountOthersQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof AccountOthersData;
  isDate?: boolean;
}

const COMPANY_INFO_FIELDS: FieldMapping[] = [
  { apiField: 'creditMarkYes', formField: 'creditMarkYes' },
  { apiField: 'division', formField: 'division' },
  { apiField: 'sicCode', formField: 'sicCode' },
  { apiField: 'fundManagerType', formField: 'fundManagerType' },
  { apiField: 'tickerSymbol', formField: 'tickerSymbol' },
  { apiField: 'crmUrl', formField: 'crmUrl' },
  { apiField: 'nameWithParent', formField: 'nameWithParent' },
  { apiField: 'isCais', formField: 'isCais' },
  { apiField: 'fax', formField: 'fax' },
  { apiField: 'limitedPartner', formField: 'limitedPartner' },
  { apiField: 'mainPhone', formField: 'mainPhone' },
  { apiField: 'summaryNotes', formField: 'summaryNotes' },
  { apiField: 'nextStepsPlugin', formField: 'nextStepsPlugin' },
  {
    apiField: 'ndaSignedDate',
    formField: 'ndaSignedDate',
    isDate: true
  }
];

function getChangedValues(
  fields: FieldMapping[],
  current: AccountOthersData,
  initial: AccountOthersData
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const { apiField, formField, isDate } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    const hasChanged = isDate
      ? toDateInputValue(currentVal as string) !==
        toDateInputValue(initialVal as string)
      : currentVal !== initialVal;

    if (hasChanged) {
      if (isDate) {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      } else if (typeof currentVal === 'boolean') {
        changes[apiField] = currentVal;
      } else {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      }
    }
  }

  return changes;
}

function toDateInputValue(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
}

const UPDATE_ACCOUNT = gql`
  mutation UpdateAccount($id: ID!, $values: AccountInput!) {
    updateAccount(id: $id, values: $values) {
      ok
      account {
        id
        creditMarkYes
        division
        sicCode
        fundManagerType
        tickerSymbol
        crmUrl
        nameWithParent
        isCais
        fax
        limitedPartner
        parentAccountId {
          id
          name
        }
        mainPhone
        summaryNotes
        nextStepsPlugin
        ndaSignedDate
      }
    }
  }
`;

export interface EditCompanyInfoDialogProps {
  accountId: string;
  initialValue: AccountOthersData;
  isOpen: boolean;
  onChange: (data: AccountOthersData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: AccountOthersData;
}

export function EditCompanyInfoDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditCompanyInfoDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateAccount] = useMutation(UPDATE_ACCOUNT, {
    refetchQueries: [
      { query: GET_ACCOUNT_OTHERS, variables: { id: accountId } }
    ]
  });

  const [parentAccountIdValue, setParentAccountIdValue] = useState<string>('');

  const changes = getChangedValues(COMPANY_INFO_FIELDS, value, initialValue);

  // Handle parentAccountId separately since we need to track the ID
  const parentAccountChanged =
    value.parentAccountId !== initialValue.parentAccountId;
  if (parentAccountChanged) {
    changes.parentAccountId = parentAccountIdValue || null;
  }

  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof AccountOthersData>(
    field: K,
    fieldValue: AccountOthersData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateAccount({
        variables: { id: accountId, values: changes }
      });

      const data = response.data as {
        updateAccount?: { ok?: boolean };
      };

      if (data.updateAccount?.ok === false) {
        toast.error('Failed to save company info');
      } else {
        toast.success('Company info saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving company info:', error);
      toast.error('Failed to save company info');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Company Info"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditFormField
          label="CreditMarkYes"
          onChange={creditMarkYes => {
            updateField('creditMarkYes', creditMarkYes);
          }}
          value={value.creditMarkYes}
        />
        <EditDynamicSelectField
          fieldName="division"
          label="Division"
          model="opstream.account"
          onChange={v => {
            updateField('division', v);
          }}
          value={value.division}
        />
        <EditFormField
          label="SIC Code"
          onChange={sicCode => {
            updateField('sicCode', sicCode);
          }}
          value={value.sicCode}
        />
        <EditDynamicSelectField
          fieldName="fund_manager_type"
          label="Fund Manager Type"
          model="opstream.account"
          onChange={v => {
            updateField('fundManagerType', v);
          }}
          value={value.fundManagerType}
        />
        <EditFormField
          label="Ticker Symbol"
          onChange={tickerSymbol => {
            updateField('tickerSymbol', tickerSymbol);
          }}
          value={value.tickerSymbol}
        />
        <EditFormField
          label="CRM URL"
          onChange={crmUrl => {
            updateField('crmUrl', crmUrl);
          }}
          type="url"
          value={value.crmUrl}
        />
        <EditFormField
          label="NDA Signed Date"
          onChange={ndaSignedDate => {
            updateField('ndaSignedDate', ndaSignedDate);
          }}
          type="date"
          value={toDateInputValue(value.ndaSignedDate)}
        />
        <EditFormField
          label="Name With Parent"
          onChange={nameWithParent => {
            updateField('nameWithParent', nameWithParent);
          }}
          value={value.nameWithParent}
        />
        <EditCheckboxField
          checked={value.isCais}
          label="Is CAIS"
          onCheckedChange={isCais => {
            updateField('isCais', isCais);
          }}
        />
        <EditFormField
          label="Fax"
          onChange={fax => {
            updateField('fax', fax);
          }}
          value={value.fax}
        />
        <EditCheckboxField
          checked={value.limitedPartner}
          label="Limited Partner"
          onCheckedChange={limitedPartner => {
            updateField('limitedPartner', limitedPartner);
          }}
        />
        <EditAccountSelectField
          currentAccountId={accountId}
          label="Parent Account"
          onChange={(id, name) => {
            setParentAccountIdValue(id);
            onChange({
              ...value,
              parentAccountId: name
            });
          }}
          value={
            value.parentAccountId
              ? {
                  id: parentAccountIdValue || '',
                  name: value.parentAccountId
                }
              : null
          }
        />
        <EditFormField
          label="Main Phone"
          onChange={mainPhone => {
            updateField('mainPhone', mainPhone);
          }}
          value={value.mainPhone}
        />
        <div className="col-span-2">
          <EditTextareaField
            label="Summary Notes"
            onChange={summaryNotes => {
              updateField('summaryNotes', summaryNotes);
            }}
            rows={4}
            value={value.summaryNotes}
          />
        </div>
        <div className="col-span-2">
          <EditTextareaField
            label="Next Steps Plugin"
            onChange={nextStepsPlugin => {
              updateField('nextStepsPlugin', nextStepsPlugin);
            }}
            rows={4}
            value={value.nextStepsPlugin}
          />
        </div>
      </div>
    </EditDialog>
  );
}
